import React from 'react';
import PropTypes from 'prop-types';
import { CheckDiffText } from '../../../common/utils';

const StevedoringData = ({ serviceProvider }) => {
    const shouldMap = React.useMemo(() => {
        if (
            serviceProvider.provider && 
            serviceProvider.provider.limitations &&
            (serviceProvider.provider.limitations.length > serviceProvider.limitations.length)
        ) {
            return serviceProvider.provider.limitations;
        }
        return serviceProvider.limitations;
    }, [serviceProvider]);

    const renderNormalView = () => (
        <div className="paper-block" style={{ backgroundColor: 'inherit' }}>
            <div className='title'>{serviceProvider.activities.join(',')}</div>
            {serviceProvider.limitations.map((limitation, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span style={{ wordBreak: 'break-all', display: 'inline' }}>{limitation.berth}</span>
                    </div>
                    <div className="limitation">
                        Max draft <span>{limitation.maxDraught} m</span>
                    </div>
                    <div className="limitation">
                        Max beam <span>{limitation.maxBeam} m</span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>{limitation.maxLOA} m</span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>{limitation.maxDWT} m</span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>{limitation.channelDepth} m</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRemoveView = () => (
        <div className="paper-block">
            <div className='title'>
                <CheckDiffText
                    oldValue={serviceProvider.activities.join(',')}
                    newValue={null}
                />
            </div>
            {serviceProvider.limitations.map((limitation, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span>
                            <CheckDiffText
                                oldValue={limitation.berth}
                                newValue={null}
                            />
                        </span>
                    </div>
                    <div className="limitation">
                        Max draft <span>
                            <CheckDiffText
                                oldValue={limitation.maxDraught}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max beam <span>
                            <CheckDiffText
                                oldValue={limitation.maxBeam}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>
                            <CheckDiffText
                                oldValue={limitation.maxLOA}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>
                            <CheckDiffText
                                oldValue={limitation.maxDWT}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>
                            <CheckDiffText
                                oldValue={limitation.channelDepth}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderChangeView = () => (
        <div className="paper-block" style={{ backgroundColor: 'inherit' }}>
            <div className='title'>
                <CheckDiffText
                    oldValue={serviceProvider.activities.join(',')}
                    newValue={(serviceProvider.provider.activities || []).join(',')}
                />
            </div>
            {shouldMap.map((_, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span style={{ wordBreak: 'break-all', display: 'inline' }}>
                            <CheckDiffText
                                oldValue={
                                    serviceProvider.limitations[index] && serviceProvider.limitations[index].berth || ''
                                }
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].berth || null}
                            />
                        </span>
                    </div>
                    <div className="limitation">
                        Max draft <span>
                            <CheckDiffText
                                oldValue={serviceProvider.limitations[index] && serviceProvider.limitations[index].maxDraught || ''}
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].maxDraught || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max beam <span>
                            <CheckDiffText
                                oldValue={serviceProvider.limitations[index] && serviceProvider.limitations[index].maxBeam || ''}
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].maxBeam || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>
                            <CheckDiffText
                                oldValue={serviceProvider.limitations[index] && serviceProvider.limitations[index].maxLOA || ''}
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].maxLOA || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>
                            <CheckDiffText
                                oldValue={serviceProvider.limitations[index] && serviceProvider.limitations[index].maxDWT || ''}
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].maxDWT || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>
                            <CheckDiffText
                                oldValue={serviceProvider.limitations[index] && serviceProvider.limitations[index].channelDepth || ''}
                                newValue={serviceProvider.provider.limitations[index] && serviceProvider.provider.limitations[index].channelDepth || null}
                            /> m
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
    return (
        <React.Fragment>
            {(serviceProvider.status === 'new' || serviceProvider.status === 'normal') && renderNormalView()}
            {serviceProvider.status === 'remove' && renderRemoveView()}
            {serviceProvider.status === 'change' && renderChangeView()}
        </React.Fragment>
    );
};

StevedoringData.propTypes = {
    serviceProvider: PropTypes.shape({
        status: PropTypes.oneOf(['new', 'normal', 'remove', 'change']).isRequired,
        activities: PropTypes.arrayOf(PropTypes.string).isRequired,
        limitations: PropTypes.arrayOf(PropTypes.shape({
            berth: PropTypes.string,
            maxDraught: PropTypes.string,
            maxBeam: PropTypes.string,
            maxLOA: PropTypes.string,
            maxDWT: PropTypes.string,
            channelDepth: PropTypes.string,
        })).isRequired,
        provider: PropTypes.shape({
            activities: PropTypes.arrayOf(PropTypes.string),
            limitations: PropTypes.array,
        }),
    }).isRequired,
};

export default StevedoringData;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersApproval/StevedoringData.jsx